/* eslint-disable no-console */
import type { MobileAppVersion } from '#src/lib/storage/sessionStorage.ts';
import {
  IOsMobileAppVersion,
  sessionStorage,
} from '#src/lib/storage/sessionStorage.ts';
import * as nativeMessages from '#src/protos/webview/channel/native_messages.ts';
import type { StringKeysOf } from '#src/types/index.ts';

import { useFeatureFlags } from './useFeatureFlags.tsx';

export const ENTER_FROM_ANDROID_MARKETPLACE_TAB_PARAM =
  'enterFromAndroidMarketplaceTab';

const US_STOREFRONT_COUNTRY_CODE = 'USA' as const;

declare global {
  interface Window {
    MobileAppChannel?: {
      postMessage: (message: any) => void;
    };
    isFlutterInAppWebViewReady?: boolean;
    flutter_inappwebview?: {
      callHandler: (handlerName: string, message: any) => void;
    };
    webkit?: {
      messageHandlers: {
        iosAppChannel: {
          postMessage: (message: any) => void;
        };
      };
    };
  }
}

type messages = StringKeysOf<nativeMessages.ChannelMessage>;

export const parseMobileApp = (
  isMobileApp: MobileAppVersion | null | undefined
) => {
  if (!isMobileApp) {
    return { platform: 'native-web' };
  }
  const androidAppVersion = +isMobileApp || undefined;
  const iosAppVersion =
    !androidAppVersion && isMobileApp.includes('ios')
      ? (isMobileApp as IOsMobileAppVersion)
      : null;
  const isGoldApp = !!iosAppVersion?.includes('gold');
  let platform = 'native-web';
  if (androidAppVersion) {
    platform = 'android';
  }
  if (iosAppVersion) {
    platform = isGoldApp ? 'ios-gold' : 'ios';
  }

  return { iosAppVersion, isGoldApp, androidAppVersion, platform };
};

const postMessage = <K extends messages>(
  message: K,
  params?: nativeMessages.ChannelMessage[K]
) => {
  const { androidAppVersion, iosAppVersion } = parseMobileApp(
    sessionStorage.getItem('mobileApp')
  );
  const jsonMessage = nativeMessages.ChannelMessage.toJSON({
    [message]: params || {},
  } as any);

  if (
    androidAppVersion &&
    (androidAppVersion === 1 || androidAppVersion === 2)
  ) {
    if (window.MobileAppChannel) {
      window.MobileAppChannel.postMessage(JSON.stringify(jsonMessage));
    } else {
      console.error(
        'MobileAppChannel is not defined, cannot post',
        jsonMessage
      );
    }
  } else if (androidAppVersion && androidAppVersion >= 3) {
    if (window.isFlutterInAppWebViewReady && window.flutter_inappwebview) {
      try {
        window.flutter_inappwebview.callHandler(
          'channelMessageHandler',
          jsonMessage
        );
      } catch {
        // usually a `window.flutter_inappwebview._callHandler is not a function` error
        // really there is not much we can do, the internal `_callHandler` might not be ready or so
      }
    } else if (!window.flutter_inappwebview) {
      console.error(
        'flutter_inappwebview is not defined, cannot call handler',
        jsonMessage
      );
    } else {
      console.error(
        'flutter_inappwebview is not ready, cannot call handler',
        jsonMessage
      );
    }
  } else if (
    iosAppVersion &&
    window?.webkit?.messageHandlers?.iosAppChannel?.postMessage
  ) {
    window.webkit.messageHandlers.iosAppChannel.postMessage(jsonMessage);
  } else {
    console.error('Not a mobile app, cannot postMessage', jsonMessage);
  }
};

//ANDROID_APP_BUILD_VERSION
const ANDROID_APP_BUILD_VERSION_NATIVE_OAUTH_SIGNUP_THRESHOLD = 178;
const ANDROID_APP_BUILD_VERSION_NATIVE_SEARCH = 181;
const ANDROID_APP_BUILD_VERSION_OPEN_BROWSER = 220;

//IOS_APP_BUILD_VERSION
const IOS_APP_BUILD_VERSION_CAN_USE_POST_MESSAGE_AND_FOR_YOU_THRESHOLDS = {
  [IOsMobileAppVersion.IOS_1]: 238,
  [IOsMobileAppVersion.IOS_GOLD]: 227,
};

const IOS_APP_BUILD_VERSION_CAN_USE_NATIVE_CARD_PAGE = {
  [IOsMobileAppVersion.IOS_1]: 259,
  [IOsMobileAppVersion.IOS_GOLD]: 254,
};

const IOS_APP_BUILD_VERSION_CAN_USE_OPEN_CART_AND_CLOSE_CURRENT_WEBVIEW = {
  [IOsMobileAppVersion.IOS_1]: 387,
  [IOsMobileAppVersion.IOS_GOLD]: 562,
};

const SLOW_MODE_DEVICE_MEMORY_THRESHOLD = 4;

export const useIsMobileApp = () => {
  const isMobileApp = sessionStorage.getItem('mobileApp');
  const twaVersionNumber = sessionStorage.getItem('twa');
  const isTwa = twaVersionNumber !== null;
  const appBuildVersionNumber =
    sessionStorage.getItem('appBuildVersionNumber') ||
    // locally, always assume we're running the latest build, except when explicitly set
    (import.meta.env.MODE === 'development' && 9999);
  const googlePlayStoreCountryCode = sessionStorage.getItem(
    'googlePlayStoreCountryCode'
  );
  const storefrontCountryCode = sessionStorage.getItem('storefrontCountryCode');
  const {
    flags: {
      useIosMarketAuthorizedCountries,
      useAuthorizeIosUsaToBuyFromWeb,
      useAuthorizeAndroidUsaToBuyFromWeb,
    },
  } = useFeatureFlags();
  const { countries: iosMarketAuthorizedCountries } =
    useIosMarketAuthorizedCountries || { countries: [] };

  const { androidAppVersion, isGoldApp, iosAppVersion, platform } =
    parseMobileApp(isMobileApp);
  const isAndroidApp = !!androidAppVersion || isTwa;
  const isIosApp = !!iosAppVersion;

  // Experimental: detect slow device by checking device memory
  // When `deviceMemory` is not available, this will default to 0
  const slowDevice =
    +('deviceMemory' in navigator && (navigator as any).deviceMemory) <=
    SLOW_MODE_DEVICE_MEMORY_THRESHOLD;

  const nativeOAuthSignup =
    isAndroidApp &&
    appBuildVersionNumber &&
    appBuildVersionNumber >=
      ANDROID_APP_BUILD_VERSION_NATIVE_OAUTH_SIGNUP_THRESHOLD;

  const canUseIosPostMessageAndForYou = !!(
    isIosApp &&
    appBuildVersionNumber &&
    appBuildVersionNumber >=
      IOS_APP_BUILD_VERSION_CAN_USE_POST_MESSAGE_AND_FOR_YOU_THRESHOLDS[
        isGoldApp ? IOsMobileAppVersion.IOS_GOLD : IOsMobileAppVersion.IOS_1
      ]
  );

  const canUseIosNativeCardPage = !!(
    isIosApp &&
    appBuildVersionNumber &&
    appBuildVersionNumber >=
      IOS_APP_BUILD_VERSION_CAN_USE_NATIVE_CARD_PAGE[
        isGoldApp ? IOsMobileAppVersion.IOS_GOLD : IOsMobileAppVersion.IOS_1
      ]
  );

  const canOpenCartOpenPaymentBoxAndCloseCurrentWebview = !!(
    isIosApp &&
    appBuildVersionNumber &&
    appBuildVersionNumber >=
      IOS_APP_BUILD_VERSION_CAN_USE_OPEN_CART_AND_CLOSE_CURRENT_WEBVIEW[
        isGoldApp ? IOsMobileAppVersion.IOS_GOLD : IOsMobileAppVersion.IOS_1
      ]
  );

  const isMarketAuthorizedIosApp =
    (iosMarketAuthorizedCountries.length > 0 &&
      isIosApp &&
      iosMarketAuthorizedCountries.includes(storefrontCountryCode || '')) ||
    (useAuthorizeIosUsaToBuyFromWeb &&
      storefrontCountryCode === US_STOREFRONT_COUNTRY_CODE &&
      isIosApp);

  const isUSAndroidApp =
    useAuthorizeAndroidUsaToBuyFromWeb &&
    isAndroidApp &&
    googlePlayStoreCountryCode === US_STOREFRONT_COUNTRY_CODE;

  const canOpenBrowser =
    isAndroidApp &&
    appBuildVersionNumber &&
    appBuildVersionNumber >= ANDROID_APP_BUILD_VERSION_OPEN_BROWSER;

  return {
    isMobileApp: !!isMobileApp,
    isTwa,
    twaVersionNumber,
    isIosApp,
    isMarketAuthorizedIosApp,
    isAndroidApp,
    isUSAndroidApp,
    googlePlayStoreCountryCode,
    hideBottomNavBar: isIosApp,
    postMessage,
    slowDevice,
    nativeOAuthSignup,
    useNativeSearch:
      isAndroidApp &&
      appBuildVersionNumber &&
      appBuildVersionNumber >= ANDROID_APP_BUILD_VERSION_NATIVE_SEARCH,
    consentTracking: true, // TODO: implement consent tracking natively and forward the value here
    canUseIosPostMessageAndForYou,
    canUseIosNativeCardPage,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
    appBuildVersionNumber,
    platform,
    postOpenCurrentUrlMessage: canOpenBrowser
      ? (cardSlug?: string) =>
          postMessage('openBrowser', {
            // oxlint-disable-next-line sorare/no-window-location
            url: `${window.location.origin}${window.location.pathname}?${ENTER_FROM_ANDROID_MARKETPLACE_TAB_PARAM}=${appBuildVersionNumber}${cardSlug ? `&card=${cardSlug}` : ''}`,
          })
      : undefined,
    postOpenCurrentUrlWithParamsMessage: canOpenBrowser
      ? (params: Record<string, string>) =>
          postMessage('openBrowser', {
            // oxlint-disable-next-line sorare/no-window-location
            url: `${window.location.origin}${window.location.pathname}${
              params
                ? `?${Object.entries(params)
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}`
                : ''
            }`,
          })
      : undefined,
  };
};
